import GppGoodIcon from "@mui/icons-material/GppGood";
import React from "react";
import Tooltip from "@mui/material/Tooltip";
import WarningIcon from "@mui/icons-material/Warning";
import { formatNumberToEnglish, parseGermanFloat } from "./helper";

//-----------------------------------------------------
//----Mikro light data
//-----------------------------------------------------
/**
 * Transforms the Mikro threshold object into an array of key-value pairs.
 * 
 * @param {object} mikroThreshold - Threshold data for Mikro values.
 * @returns {Array<{key: string, value: any}>} Array of transformed key-value pairs.
 */
export const getTransformedData = (mikroThreshold: any) => {
  if (!mikroThreshold || typeof mikroThreshold !== "object") {
    // Return an empty array or handle the case when mikroThreshold is undefined or not an object.
    return [];
  }

  // Create the "pseudomonas" object
  const pseudomonasObject = {
    critical_pseudomonas: mikroThreshold?.critical_pseudomonas,
    high_pseudomonas: mikroThreshold?.high_pseudomonas,
  };

  // Add the "pseudomonas" object to the transformedData
  mikroThreshold.pseudomonas = pseudomonasObject;

  const transformedData = Object.entries(mikroThreshold).map(
    ([key, value]) => ({
      key,
      value,
    })
  );

  return transformedData;
};

/**
 * Determines the color, icon, and tooltip for Mikro value based on thresholds.
 * 
 * @param {any} value - The value to evaluate.
 * @param {any} mikroData - Threshold and configuration data.
 * @param {any} colors - Color palette configuration.
 * @returns {object} Border color, icon, and tooltip info.
 */
export const determineMikroValueColor = (
  value: any,
  mikroData: any,
  colors: any
) => {
  if (!mikroData) {
    // Handle the case where mikroData is undefined
    return {
      color: colors.blueAccent[600],
      icon: null,
      tooltipText: "No data available.",
    };
  }

  const numberValue: number = parseGermanFloat(value);

  if (mikroData && mikroData.key === "pseudomonas" && mikroData.value) {
    const high_pseudomonas = parseFloat(mikroData.value.high_pseudomonas);
    const criticalPseudomonas = parseFloat(
      mikroData.value.critical_pseudomonas
    );

    let tooltipText = "";
    let color = colors.blueAccent[600];
    let icon = null;

    if (numberValue > criticalPseudomonas) {
      tooltipText = "Sofortmaßnahme";
      color = "red";
      icon = <WarningIcon sx={{ color: "red" }} />;
    } else if (
      numberValue > high_pseudomonas &&
      numberValue <= criticalPseudomonas
    ) {
      tooltipText = "Kontrolle";
      color = "yellow";
      icon = <WarningIcon sx={{ color: "yellow" }} />;
    }else if(numberValue <= high_pseudomonas){
      color = "green";
      icon = <GppGoodIcon sx={{ color: "green" }} />;
      tooltipText = "This is the normal range.";
    }

    return {
      color,
      icon,
      tooltipText,
    };
  } else if (mikroData && mikroData.value) {
    
    const mikro_value = parseFloat(mikroData.value);
    let tooltipText = "";
    if (numberValue >= mikro_value) {
      tooltipText = "Dies ist der Warnbereich.";
      return {
        color: "red",
        icon: <WarningIcon sx={{ color: "red" }} />,
        tooltipText,
      };
    }else if(numberValue < mikro_value){
      return {
        color: "green",
        icon: <GppGoodIcon sx={{ color: "green" }} />,
        tooltipText: "This is the normal range.",
      };
    }
  }

  return {
    color: colors.blueAccent[600],
    icon: null,
    tooltipText: "No data available.",
  };
};

/**
 * Adjusts value based on comparison operator for traffic light calculation.
 * For "<" operator: subtract 1 from value (e.g., <100 means max is 99)
 * For ">" operator: add 1 to value (e.g., >100 means min is 101)
 * 
 * @param {any} value - The original value
 * @param {string} comparisonOperator - The comparison operator (<, >, =)
 * @returns {any} The adjusted value for traffic light calculation
 */
const adjustValueForComparison = (value: any, comparisonOperator?: string): any => {
  // Return original value if empty, null, undefined, or empty string
  if (value === null || value === undefined || value === "") {
    return value;
  }
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return value;
  
  if (comparisonOperator === "<") {
    return numValue - 1;
  } else if (comparisonOperator === ">") {
    return numValue + 1;
  }
  return numValue;
};

/**
 * Tooltip component displaying Mikro value warning or status.
 * 
 * @param {object} props - Props containing value, trafficData, colors, and optional comparisonOperator.
 * @returns {JSX.Element} Tooltip with warning or good icon.
 */
export const MikroDataTooltip = (props: any) => {
  const { value, trafficData, colors, comparisonOperator } = props;
  
  // Adjust value based on comparison operator for traffic light calculation
  // Only adjust for non-main building plants (when comparison is not "=")
  const adjustedValue = comparisonOperator && comparisonOperator !== "=" 
    ? adjustValueForComparison(value, comparisonOperator)
    : value;
    
  const borderColorInfo = determineMikroValueColor(adjustedValue, trafficData, colors);
  return (
    <Tooltip
      style={{
        cursor: "pointer",
        fontSize: "20px",
      }}
      title={borderColorInfo.tooltipText}
    >
      <div>{borderColorInfo.icon}</div>
    </Tooltip>
  );
};

//-----------------------------------------------------
//----Traffic light data
//-----------------------------------------------------
/**
 * Checks if the given value is not null or undefined.
 * 
 * @param {any} value - Value to check.
 * @returns {boolean} True if value exists.
 */
const isNotNull = (value: any) => {
  if (value === null || value === undefined) return false;
  return true;
};

/**
 * Checks if the number falls into warning range.
 */
const isWarning = (
  numberValue: number,
  low_warning_min: number,
  normal_min: number,
  high_warning_min: number,
  high_risk_min: number
) => {
  return (
    (isNotNull(low_warning_min) &&
      numberValue >= low_warning_min &&
      numberValue < normal_min) ||
    (isNotNull(high_warning_min) &&
      numberValue >= high_warning_min &&
      numberValue < high_risk_min)
  );
};

const isDipSlideWarning = (
  numberValue: number,
  low_warning_min: number,
  normal_min: number,
  high_warning_min: number,
  high_risk_min: number
) => {
  return (
    (isNotNull(high_warning_min) &&
      numberValue > high_warning_min &&
      numberValue < high_risk_min)
  );
};

/**
 * Checks if the number falls into risk range.
 */
const isRisk = (
  numberValue: number,
  low_risk_min: number,
  low_warning_min: number,
  normal_min: number,
  high_risk_min: number,
  high_risk_max: number
) => {  
  return (
    (isNotNull(low_risk_min) &&
      numberValue >= low_risk_min &&
      numberValue < low_warning_min) ||
    (isNotNull(low_risk_min) &&
      !isNotNull(low_warning_min) &&
      numberValue >= low_risk_min &&
      numberValue < normal_min) ||
    (numberValue >= high_risk_min && numberValue < high_risk_max) ||
    (!isNotNull(high_risk_max) && numberValue >= high_risk_min)
  );
};

/**
 * Checks if the number falls into normal (safe) range.
 */
const isNormal = (
  numberValue: number,
  normal_min: number,
  high_warning_min: number,
  high_risk_min: number
) => {
  return (
    numberValue >= normal_min &&
    ((isNotNull(high_warning_min) && numberValue < high_warning_min) ||
      (!isNotNull(high_warning_min) && numberValue < high_risk_min))
  );
};

const isDipSlideNormal = (
  numberValue: number,
  normal_min: number,
  high_risk_min: number,
) => {
  return (
    numberValue >= normal_min &&
    (isNotNull(high_risk_min) && numberValue <= high_risk_min) 
  );
};
/**
 * Determines border color, icon, and tooltip text based on traffic light rules.
 * 
 * @param {any} value - The value to evaluate.
 * @param {any} trafficData - Traffic light thresholds and rules.
 * @param {any} colors - Color palette configuration.
 * @returns {object} Color, icon, and tooltipText
 */
export const determineBorderColor = (
  value: any,
  trafficData: any,
  colors: any
) => {
  if (!trafficData) {
    // Handle the case where trafficData is undefined
    return {
      color: colors.blueAccent[600],
      icon: null,
      tooltipText: "No data available.",
    };
  }

  let tooltipText = "";

  const numberValue: number = formatNumberToEnglish(value);

  if (
    isRisk(
      numberValue,
      trafficData["low_risk_min"],
      trafficData["low_warning_min"],
      trafficData["normal_min"],
      trafficData["high_risk_min"],
      trafficData["high_risk_max"]
    )
  ) {
    tooltipText = "This is the risk range.";
    return {
      color: "red",
      icon: <WarningIcon sx={{ color: "red" }} />,
      tooltipText,
    };
  } else if (
    isWarning(
      numberValue,
      trafficData["low_warning_min"],
      trafficData["normal_min"],
      trafficData["high_warning_min"],
      trafficData["high_risk_min"]
    )
  ) {
    return {
      color: "orange",
      icon: <WarningIcon sx={{ color: "orange" }} />,
      tooltipText: "This is the warning range.",
    };
  } else if (
    isNormal(
      numberValue,
      trafficData["normal_min"],
      trafficData["high_warning_min"],
      trafficData["high_risk_min"]
    )
  ) {
    return {
      color: "green",
      icon: <GppGoodIcon sx={{ color: "green" }} />,
      tooltipText: "This is the normal range.",
    };
  } else {
    return {
      color: colors.blueAccent[600],
      icon: null,
      tooltipText: "No data available.",
    };
  }
};

/**
 * Maps a backend-computed VDI lab parameter status to the same icon/tooltip used by
 * determineBorderColor, so VDI plants render visually-identical traffic-light icons.
 *
 * @param {string} [status] - One of 'red' | 'yellow' | 'green' | 'none'.
 * @returns {{ color: string, icon: JSX.Element | null, tooltipText: string }}
 */
export const mapStatusToIcon = (status?: string) => {
  switch (status) {
    case "red":
      return {
        color: "red",
        icon: <WarningIcon sx={{ color: "red" }} />,
        tooltipText: "This is the risk range.",
      };
    case "yellow":
      return {
        color: "orange",
        icon: <WarningIcon sx={{ color: "orange" }} />,
        tooltipText: "This is the warning range.",
      };
    case "green":
      return {
        color: "green",
        icon: <GppGoodIcon sx={{ color: "green" }} />,
        tooltipText: "This is the normal range.",
      };
    default:
      return { color: "", icon: null, tooltipText: "" };
  }
};

/**
 * Tooltip component displaying Traffic Light value status.
 * 
 * @param {object} props - Props containing value, trafficData, and colors.
 * @returns {JSX.Element} Tooltip with warning, risk or safe icon.
 */
export const TrafficDataTooltip = (props: any) => {
  const { value, trafficData, colors } = props;
  const newValue = typeof value === "string" ? value.replace(".", ",") : value;
  const borderColorInfo = determineBorderColor(newValue, trafficData, colors);

  return (
    <Tooltip
      style={{
        cursor: "pointer",
        fontSize: "20px",
      }}
      title={borderColorInfo.tooltipText}
    >
      <div>{borderColorInfo.icon}</div>
    </Tooltip>
  );
};


export const DipSlideTrafficDataTooltip = (props: any) => {
  
  const { value, trafficData, colors } = props;
  const newValue = typeof value === "string" ? value.replace(".", ",") : value;
  
  const borderColorInfo = determineDipSlideBorderColor(newValue, trafficData, colors);

  return (
    <Tooltip
      style={{
        cursor: "pointer",
        fontSize: "20px",
      }}
      title={borderColorInfo.tooltipText}
    >
      <div>{borderColorInfo.icon}</div>
    </Tooltip>
  );
};

export const determineDipSlideBorderColor = (
  value: any,
  trafficData: any,
  colors: any
) => {
  if (!trafficData) {
    // Handle the case where trafficData is undefined
    return {
      color: colors.blueAccent[600],
      icon: null,
      tooltipText: "No data available.",
    };
  }

  let tooltipText = "";

  const numberValue: number = formatNumberToEnglish(value);

  if(isNaN( numberValue))
  {
    return {
      color: colors.blueAccent[600],
      icon: null,
      tooltipText: "No data available.",
    };
  }
  
  if (
    isRisk(
      numberValue,
      trafficData["low_risk_min"],
      trafficData["low_warning_min"],
      trafficData["normal_min"],
      trafficData["high_risk_min"],
      trafficData["high_risk_max"]
    )
  ) {
    tooltipText = "This is the risk range.";
    return {
      color: "red",
      icon: <WarningIcon sx={{ color: "red" }} />,
      tooltipText,
    };
  } else {
    return {
      color: "green",
      icon: <GppGoodIcon sx={{ color: "green" }} />,
      tooltipText: "This is the normal range.",
    };
  }
};
